import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Users, ChevronRight, UserPlus } from "lucide-react";
import { api } from "@/lib/api";
import { Avatar } from "@/components/Avatar";

// "Your crew" — a quick avatar row so students can jump to friends around them.
export default function CrewStrip() {
  const navigate = useNavigate();
  const [friends, setFriends] = useState(null);

  useEffect(() => {
    api.get("/presence/friends").then((r) => setFriends(r.data.friends || [])).catch(() => setFriends([]));
  }, []);

  if (friends === null) return null;
  const freeCount = friends.filter((f) => f.free_now).length;

  return (
    <motion.button
      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      onClick={() => navigate("/friends")}
      data-testid="crew-strip"
      className="mb-5 w-full text-left bg-white border border-border rounded-2xl p-4 flex items-center gap-4 hover:border-brand transition-colors group"
    >
      {friends.length > 0 ? (
        <div className="flex -space-x-3 shrink-0">
          {friends.slice(0, 6).map((f) => (
            <div key={f.user_id} className="relative" data-testid={`crew-avatar-${f.user_id}`}>
              <Avatar src={f.picture} name={f.name} size={40} className="ring-2 ring-white" />
              {f.free_now && (
                <span data-testid={`crew-free-${f.user_id}`} title={`${f.name} is free now`}
                  className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-[#16A34A] ring-2 ring-white" />
              )}
            </div>
          ))}
          {friends.length > 6 && (
            <div className="w-10 h-10 rounded-full ring-2 ring-white bg-secondary text-foreground text-xs font-bold flex items-center justify-center">
              +{friends.length - 6}
            </div>
          )}
        </div>
      ) : (
        <div className="w-11 h-11 rounded-full bg-brand/10 text-brand flex items-center justify-center shrink-0">
          <Users size={20} strokeWidth={1.7} />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <div className="font-semibold text-sm flex items-center gap-1.5">
          {friends.length > 0 ? "Your crew" : "Build your crew"}
        </div>
        <div className="text-xs text-muted-foreground">
          {friends.length > 0
            ? (freeCount > 0 ? `${freeCount} free right now · see where to meet up` : "See who's free and where to meet up")
            : "Add friends to see who's free between classes"}
        </div>
      </div>
      <span className="flex items-center gap-1 text-xs font-semibold text-brand shrink-0">
        {friends.length > 0 ? "Open" : <><UserPlus size={13} strokeWidth={2} /> Add</>}
        <ChevronRight size={15} strokeWidth={2} className="group-hover:translate-x-0.5 transition-transform" />
      </span>
    </motion.button>
  );
}
